import * as React from 'react';

import ActionButton from 'components/buttons/action-button/ActionButton';
import TutorialCarouselSlide from './TutorialCarouselSlide';
import TutorialCarouselSlideGeneric from './TutorialCarouselSlideGeneric';
import TutorialCarouselSlideCustom from './TutorialCarouselSlideCustom';
import { ModalFrame, ModalFrameFooter, ModalFrameInner } from 'components/modal';

import { __t, applyTranslations } from 'utils/language';

import type { OrganizationProfileType } from 'types';
import type { TutorialCarouselSlide as TutorialCarouselSlideAlias } from './TutorialCarouselSlide.type';

applyTranslations(require('./tutorial-carousel.i18n.json'));

const slideMap = {
  custom: TutorialCarouselSlideCustom,
  generic: TutorialCarouselSlideGeneric
};

const STEP_MIN_HEIGHT = 350;

type Props = {
  slides: Array<TutorialCarouselSlideAlias>,
  showIntroSlide: boolean,
  type: OrganizationProfileType,
  slideHeight?: number,
  onCloseModal: () => void
};

type State = {
  slideIndex: number
};

class TutorialCarousel extends React.PureComponent<Props, State> {
  static defaultProps = {
    showIntroSlide: false,
    type: 'brand'
  };

  state = {
    slideIndex: 0
  };

  handleShowSlide = (event: Event): void => {
    const { slideIndex } = this.state;
    const target = event.currentTarget;
    const step = (target instanceof HTMLElement && target.dataset.step) || 0;

    this.setState({
      slideIndex: slideIndex + Number(step)
    });
  };

  renderButtons = (updatedSlides: Array<TutorialCarouselSlideAlias>): JSX.Element => {
    const { showIntroSlide, onCloseModal } = this.props;
    const { slideIndex } = this.state;
    const isLastSlide = slideIndex === updatedSlides.length - 1;
    const showBackButton = (showIntroSlide && slideIndex > 1) || (!showIntroSlide && slideIndex > 0);

    if (showIntroSlide && slideIndex === 0) {
      return (
        <ActionButton
          data={{
            step: 1,
            qa: 'tutorial-carousel-button--continue'
          }}
          onClick={this.handleShowSlide}
        >
          {__t('tutorial_carousel.button_continue_label')}
        </ActionButton>
      );
    }

    return (
      <React.Fragment>
        {
          showBackButton &&
            <ActionButton
              color="tertiary"
              data={{
                step: -1,
                qa: 'tutorial-carousel-button--back'
              }}
              onClick={this.handleShowSlide}
            >
              {__t('tutorial_carousel.button_back_label')}
            </ActionButton>
        }
        <ActionButton
          data={{
            step: 1,
            qa: isLastSlide ? 'tutorial-carousel-button--done' : 'tutorial-carousel-button--next'
          }}
          onClick={isLastSlide ? onCloseModal : this.handleShowSlide}
        >
          {__t(`tutorial_carousel.${isLastSlide ? 'button_done_label' : 'button_next_label'}`)}
        </ActionButton>
      </React.Fragment>
    );
  };

  render(): React.ReactNode {
    const { slideHeight, slides, showIntroSlide, type } = this.props;
    const { slideIndex } = this.state;
    const updatedSlides = showIntroSlide || type === 'brand' ? slides : slides.slice(1);
    const minHeight = slideHeight ?? STEP_MIN_HEIGHT;

    return (
      <ModalFrame
        stepCounter={{
          index: showIntroSlide ? slideIndex : slideIndex + 1,
          count: showIntroSlide ? updatedSlides.length - 1 : updatedSlides.length
        }}
        title={__t('tutorial_carousel.title')}
        data-qa="tutorial-carousel"
      >
        <ModalFrameInner
          minHeight={minHeight}
          align="center"
          verticalAlign="center"
        >
          {
            updatedSlides.map((slide, index) => {
              const SlideComponent: React.ElementType = slideMap[slide.type];

              return (
                <TutorialCarouselSlide
                  key={slide.key}
                  isVisible={index === slideIndex}
                >
                  <SlideComponent
                    title={slide.title}
                    body={slide.body}
                    imageName={slide.imageName}
                    iconName={slide.iconName}
                    overrides={slide.overrides}
                    extras={slide.extras}
                    qa={slide.qa}
                  />
                </TutorialCarouselSlide>
              );
            })
          }
        </ModalFrameInner>
        <ModalFrameFooter>
          {this.renderButtons(updatedSlides)}
        </ModalFrameFooter>
      </ModalFrame>
    );
  }
}

export default TutorialCarousel;
